import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useAuth } from '@/contexts/AuthContext';
import { peekPodEntry } from '@/lib/podEntry';
import { cn } from '@/lib/utils';

/**
 * Subtle bottom banner that prompts installation.
 *
 * On Chrome/Edge (Android + desktop) it triggers the native `beforeinstallprompt`
 * flow directly. iOS Safari never fires that event, so it instead routes to
 * /install, which walks through the manual "Share > Add to Home Screen" steps.
 *
 * Dismissed state is stored in sessionStorage so it doesn't reappear within
 * the same browser session.
 */
// Pages where the card must never cover the buttons or links: sign-in, sign-up and the scanner QR journey.
const QUIET_PATHS = ['/login', '/pod', '/scan-at', '/scan-ready', '/scan-setup'];

export function PWAInstallBanner() {
  const { isInstallable, isInstalled, isIOSSafari, promptInstall } = usePWAInstall();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Only once the person is signed in, and never while they are part-way through a scanner QR journey.
  const quiet = !user || QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`)) || !!peekPodEntry();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('pwa-banner-dismissed') === 'true'
  );

  if (quiet || (!isInstallable && !isIOSSafari) || isInstalled || dismissed) return null;

  const handleInstall = async () => {
    if (isIOSSafari) {
      dismiss();
      navigate('/install');
      return;
    }
    await promptInstall();
    dismiss();
  };

  const dismiss = () => {
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
    setDismissed(true);
  };

  return (
    // Sits just above the bottom navigation (h-16 plus the device's bottom inset) instead of underneath it, where the
    // navigation used to cover the bottom of the card.
    <div className="fixed left-0 right-0 z-50 px-4 pointer-events-none" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom) + 0.5rem)" }}>
      <div
        className={cn(
          "max-w-md mx-auto pointer-events-auto",
          "rounded-2xl border border-white/10 p-3",
          "flex items-center gap-3",
          "shadow-[0_-4px_40px_rgba(0,0,0,0.5)]"
        )}
        style={{
          background: "rgba(18,18,18,0.96)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-primary" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-tight">
            Add bodyCODEZ to your home screen
          </p>
          <p className="text-xs text-white/45 mt-0.5 leading-tight">
            {isIOSSafari ? 'Takes 10 seconds via Safari' : 'Instant access, works offline'}
          </p>
        </div>

        {/* Install CTA */}
        <button
          onClick={handleInstall}
          className="shrink-0 text-xs font-bold text-primary px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors"
        >
          {isIOSSafari ? 'How To' : 'Install'}
        </button>

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="shrink-0 text-white/35 hover:text-white/70 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
